// Goods Ordered Report: receipts (delivered order rows) → period columns × item rows.
// Sums are added receipt by receipt (Σ qty_i × price_i); per-unit figures are
// weighted averages derived from those sums at read time (Σ purchase / Σ qty).

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export const METRICS = [
  { key: 'qty',       label: 'Qty (pcs / lb)',       short: 'Qty',        money: false },
  { key: 'unitPrice', label: 'Purchase Unit Price',  short: 'Unit Price', money: true, perUnit: true },
  { key: 'extraUnit', label: 'Extra Cost per Unit',  short: 'Extra/Unit', money: true, perUnit: true },
  { key: 'finalUnit', label: 'Final Unit Price',     short: 'Final/Unit', money: true, perUnit: true },
  { key: 'purchase',  label: 'Purchase Total',       short: 'Purchase',   money: true },
  { key: 'extra',     label: 'Total Extra Cost',     short: 'Extra',      money: true },
  { key: 'final',     label: 'Total Final Cost',     short: 'Final',      money: true },
];

// 'YYYY-MM-DD' → { key, label }. Weeks run Monday–Sunday and are labelled by dates
// (no week numbers — the company's numbering is still being settled).
export function periodOf(date, period) {
  const [y, m, d] = date.split('-').map(Number);
  if (period === 'month') return { key: date.slice(0, 7), label: `${MONTHS[m - 1]} ${y}` };
  if (period === 'day')   return { key: date, label: `${MONTHS[m - 1]} ${d}, ${y}` };
  const start = new Date(Date.UTC(y, m - 1, d));
  start.setUTCDate(start.getUTCDate() - ((start.getUTCDay() + 6) % 7));
  const end = new Date(start);
  end.setUTCDate(start.getUTCDate() + 6);
  const f = (x) => `${MONTHS[x.getUTCMonth()]} ${x.getUTCDate()}`;
  return { key: start.toISOString().slice(0, 10), label: `${f(start)} – ${f(end)}, ${end.getUTCFullYear()}` };
}

const emptyCell = () => ({ qty: 0, purchase: 0, extra: 0, final: 0 });

function addReceipt(cell, r) {
  const purchase = r.qty * (r.unitPrice || 0);
  const extra    = r.qty * (r.extraPerUnit || 0);
  cell.qty      += r.qty;
  cell.purchase += purchase;
  cell.extra    += extra;
  cell.final    += purchase + extra;
}

export function metricValue(cell, key) {
  if (!cell) return null;
  switch (key) {
    case 'unitPrice': return cell.qty ? cell.purchase / cell.qty : null;
    case 'extraUnit': return cell.qty ? cell.extra / cell.qty : null;
    case 'finalUnit': return cell.qty ? cell.final / cell.qty : null;
    default:          return cell[key];
  }
}

// Level 3: one receipt as a cell, so the same metric columns apply to a single delivery.
export function receiptCell(r) {
  const cell = emptyCell();
  addReceipt(cell, r);
  return cell;
}

// → { periods: [{ key, label }], groups: [{ key, label, items: [{ key, name, sku, asin, unit, cells,
//      receipts: [{ ...receipt, periodKey }] }] }] }
export function buildReport(receipts, period) {
  const periods = new Map();
  const groups  = new Map();
  for (const r of receipts) {
    const p = periodOf(r.date, period);
    periods.set(p.key, p.label);
    let g = groups.get(r.group);
    if (!g) { g = { key: r.group, label: r.groupLabel, items: new Map() }; groups.set(r.group, g); }
    const ik = r.sku || r.name;
    let it = g.items.get(ik);
    if (!it) { it = { key: ik, name: r.name, sku: r.sku, asin: r.asin, unit: r.unit, cells: {}, receipts: [] }; g.items.set(ik, it); }
    it.receipts.push({ ...r, periodKey: p.key });
    if (!it.cells[p.key]) it.cells[p.key] = emptyCell();
    addReceipt(it.cells[p.key], r);
  }
  const byLabel = (a, b) => a.label.localeCompare(b.label);
  const byDate  = (a, b) => a.date.localeCompare(b.date) || (a.supplier || '').localeCompare(b.supplier || '');
  for (const g of groups.values()) for (const it of g.items.values()) it.receipts.sort(byDate);
  return {
    periods: [...periods.keys()].sort().map((key) => ({ key, label: periods.get(key) })),
    groups:  [...groups.values()].sort(byLabel).map((g) => ({
      ...g, items: [...g.items.values()].sort((a, b) => a.name.localeCompare(b.name)),
    })),
  };
}

// Group-level totals over the given (already search-filtered) items; null if none.
export function sumCells(items, periodKey) {
  let out = null;
  for (const it of items) {
    const c = it.cells[periodKey];
    if (!c) continue;
    if (!out) out = emptyCell();
    out.qty += c.qty; out.purchase += c.purchase; out.extra += c.extra; out.final += c.final;
  }
  return out;
}
